import { useState } from "react";

import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { HeartIcon, PencilIcon, ReplyIcon, Trash2Icon } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Field, FieldError } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import DeleteCommentForm from "@/features/tickets/components/DeleteCommentForm";
import { TicketCommentReplyComposer } from "@/features/tickets/components/TicketCommentReplyComposer";
import { LikeCommentSchema, UpdateCommentSchema, updateCommentSchema } from "@/features/tickets/schemas";
import { likeTicketComment, updateTicketComment } from "@/features/tickets/services/ticket";
import { TicketComment, TicketCommentLikeStatus } from "@/features/tickets/types/index";
import Error from "@/shared/components/Error";
import { PermissionTooltip } from "@/shared/components/PermissionTooltip";
import Success from "@/shared/components/Success";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { formatAbsoluteDateTime, formatRelativeDate } from "@/shared/lib/utils/date";
import { AppError } from "@/shared/lib/utils/errors";

interface TicketCommentRowProps {
  projectId: string;
  ticketId: string;
  comment: TicketComment;
  /** Whether the caller has PROJECT_TICKET_COMMENTS_CREATE for this project (replies use the same permission as comments). */
  canCreateComments: boolean;
  /** Whether the caller has PROJECT_TICKET_COMMENTS_UPDATE for this project. */
  canUpdateComments: boolean;
  /** Whether the caller has PROJECT_TICKET_COMMENTS_DELETE or PROJECT_TICKET_COMMENTS_DELETE_OWN for this project. */
  canDeleteComments: boolean;
  /** Whether the caller has PROJECT_TICKET_COMMENTS_LIKE for this project. */
  canLikeComments: boolean;
  /** Replies render at a smaller avatar size and skip further nesting (API guarantees max depth 1). */
  isReply?: boolean;
}

export function TicketCommentRow({ projectId, ticketId, comment, canCreateComments, canUpdateComments, canDeleteComments, canLikeComments, isReply = false }: TicketCommentRowProps) {
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const form = useForm<UpdateCommentSchema>({
    resolver: zodResolver(updateCommentSchema),
    defaultValues: {
      projectId,
      ticketId,
      commentId: comment.id,
      body: comment.body,
    },
  });

  const mutation = useMutation<string, AppError | Error, UpdateCommentSchema>({
    mutationFn: updateTicketComment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TICKET_COMMENTS, projectId, ticketId], exact: true });
      setIsEditing(false);
    },
  });

  const likeMutation = useMutation<TicketCommentLikeStatus, AppError | Error, LikeCommentSchema>({
    mutationFn: likeTicketComment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TICKET_COMMENTS, projectId, ticketId], exact: true });
    },
  });

  const initials = `${comment.author.firstName[0]}${comment.author.lastName[0]}`.toUpperCase();
  const name = `${comment.author.firstName} ${comment.author.lastName}`.trim();
  const relativeDate = formatRelativeDate(comment.createdAt);
  const absoluteDate = formatAbsoluteDateTime(comment.createdAt);
  const disabled = mutation.isPending || !canUpdateComments;

  function startEditing() {
    mutation.reset();
    form.reset({ projectId, ticketId, commentId: comment.id, body: comment.body });
    setIsEditing(true);
  }

  function cancelEditing() {
    form.reset({ projectId, ticketId, commentId: comment.id, body: comment.body });
    setIsEditing(false);
  }

  async function onSubmit(values: UpdateCommentSchema) {
    mutation.mutate(values);
  }

  return (
    <div className={isReply ? "pt-3" : "px-4 py-3.5"}>
      <div className="flex items-start gap-3">
        <Avatar size={isReply ? "sm" : "default"}>
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex flex-1 flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-foreground">{name}</span>
            <Tooltip delayDuration={400}>
              <TooltipTrigger asChild>
                <span className="text-[11px] text-muted-foreground">{relativeDate}</span>
              </TooltipTrigger>
              <TooltipContent>{absoluteDate}</TooltipContent>
            </Tooltip>
            {comment.updatedAt && <span className="text-[11px] italic text-muted-foreground">(edited)</span>}
            {!isEditing && (
              <div className="ml-auto flex items-center gap-1">
                <PermissionTooltip allowed={canUpdateComments} message="You don't have permission to edit comments in this project.">
                  <Button variant="ghost" size="icon-sm" className="text-muted-foreground hover:text-foreground" onClick={startEditing} disabled={!canUpdateComments} aria-label="Edit comment">
                    <PencilIcon className="size-3.5" />
                  </Button>
                </PermissionTooltip>
                <PermissionTooltip allowed={canDeleteComments} message="You don't have permission to delete comments in this project.">
                  <Button variant="ghost" size="icon-sm" className="text-muted-foreground hover:text-destructive" onClick={() => setDeleteOpen(true)} disabled={!canDeleteComments} aria-label="Delete comment">
                    <Trash2Icon className="size-3.5" />
                  </Button>
                </PermissionTooltip>
              </div>
            )}
          </div>
          {isEditing ? (
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2 pt-1">
                {mutation.isError && <Error title="Unable to update comment" error={mutation.error} />}
                <Controller
                  name="body"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Textarea {...field} placeholder="Edit your comment…" disabled={disabled} aria-invalid={fieldState.invalid} className="min-h-20" />
                      {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <div className="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="sm" type="button" onClick={cancelEditing} disabled={mutation.isPending}>
                    Cancel
                  </Button>
                  <Button size="sm" type="submit" disabled={disabled || !form.watch("body").trim()}>
                    <PencilIcon className="size-3.5" />
                    {mutation.isPending ? "Saving..." : "Save"}
                  </Button>
                </div>
              </form>
            </FormProvider>
          ) : (
            <>
              {mutation.isSuccess && <Success title="Comment updated" description="Your changes have been saved successfully." />}
              {likeMutation.isError && <Error title="Unable to like comment" error={likeMutation.error} />}
              <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">{comment.body}</p>
              <div className="flex items-center gap-3 pt-1">
                <PermissionTooltip allowed={canLikeComments} message="You don't have permission to like comments in this project.">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-auto px-0 text-xs font-medium hover:bg-transparent ${comment.isLikedByMe ? "text-primary hover:text-primary" : "text-muted-foreground hover:text-foreground"}`}
                    onClick={() => likeMutation.mutate({ projectId, ticketId, commentId: comment.id })}
                    disabled={likeMutation.isPending || !canLikeComments}
                    aria-label={comment.isLikedByMe ? "Unlike comment" : "Like comment"}
                  >
                    <HeartIcon className={`size-3 ${comment.isLikedByMe ? "fill-primary" : ""}`} />
                    {comment.likeCount > 0 && comment.likeCount}
                  </Button>
                </PermissionTooltip>
                {!isReply && !isReplying && (
                  <PermissionTooltip allowed={canCreateComments} message="You don't have permission to comment on tickets in this project.">
                    <Button variant="ghost" size="sm" className="h-auto px-0 text-xs font-medium text-muted-foreground hover:bg-transparent hover:text-foreground" onClick={() => setIsReplying(true)} disabled={!canCreateComments}>
                      <ReplyIcon className="size-3" />
                      Reply
                    </Button>
                  </PermissionTooltip>
                )}
                {!isReply && comment.replies.length > 0 && (
                  <span className="text-[10px] text-muted-foreground">
                    {comment.replies.length} {comment.replies.length === 1 ? "reply" : "replies"}
                  </span>
                )}
              </div>
              {!isReply && isReplying && <TicketCommentReplyComposer projectId={projectId} ticketId={ticketId} commentId={comment.id} canCreateComments={canCreateComments} onClose={() => setIsReplying(false)} />}
            </>
          )}
        </div>
      </div>
      <DeleteCommentForm projectId={projectId} ticketId={ticketId} comment={comment} open={deleteOpen} onOpenChange={setDeleteOpen} />
      {!isReply && comment.replies.length > 0 && (
        <div className="ml-4 flex flex-col border-l-2 border-border pl-4 mt-1">
          {comment.replies.map((reply) => (
            <TicketCommentRow key={reply.id} projectId={projectId} ticketId={ticketId} comment={reply} canCreateComments={canCreateComments} canUpdateComments={canUpdateComments} canDeleteComments={canDeleteComments} canLikeComments={canLikeComments} isReply />
          ))}
        </div>
      )}
    </div>
  );
}
